import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import { Arrow, General } from 'icons/untitledui';
import EmptyStateCard from 'shared/components/EmptyStateCard';
import SearchModal from 'teamProfiles/components/SearchModal';

// types
import { Profile } from 'teamProfiles/_types';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import * as Styled from 'teamProfiles/styles/TeamPage.style';
import * as PageLayoutStyles from 'layout/styles/PageLayout.style';
import {
  getInitials,
  getFirstName,
  getFirstNameInitial,
} from 'teamProfiles/helpers/profileUtils';

interface ViewAllProfilesProps {
  profiles: Profile[] | undefined;
  onProfileClick: (profileId: string) => void;
  isDesktopView: boolean;
  selectedProfileId: string | null;
  onAddProfileClick?: () => void;
  titleRef?: React.RefObject<HTMLDivElement>;
}

export default function TeamPage({
  profiles,
  onProfileClick,
  isDesktopView,
  selectedProfileId,
  onAddProfileClick,
  titleRef,
}: ViewAllProfilesProps): JSX.Element {
  const { t } = useTranslation('pageTeam');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const groupedProfiles = React.useMemo(() => {
    if (!profiles) return {};

    const groups: Record<string, Profile[]> = {};
    profiles.forEach((profile) => {
      const firstLetter =
        getFirstNameInitial(profile.full_name) ||
        profile.full_name.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(profile);
    });

    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => {
        const aName = getFirstName(a.full_name) || a.full_name;
        const bName = getFirstName(b.full_name) || b.full_name;
        return aName.localeCompare(bName);
      });
    });

    return groups;
  }, [profiles]);

  const sortedGroups = Object.keys(groupedProfiles).sort();

  return (
    <>
      <Styled.Container>
        {isDesktopView ? (
          <>
            <Styled.PageTitle>
              <Text.H5>{t('viewAllProfiles.team')}</Text.H5>
              <Buttons.SecondaryLightIcon
                onClick={() => setIsSearchModalOpen(true)}
                aria-label={t('viewAllProfiles.searchPlaceholder')}
                medium
              >
                <General.SearchSm size={20} />
              </Buttons.SecondaryLightIcon>
            </Styled.PageTitle>
            {onAddProfileClick && (
              <Buttons.Primary
                onClick={onAddProfileClick}
                aria-label={t('viewAllProfiles.addTeamProfile')}
                medium
              >
                <General.Plus size={20} />
                {t('viewAllProfiles.addTeamProfile')}
              </Buttons.Primary>
            )}
          </>
        ) : (
          <PageLayoutStyles.PageTitleContainer ref={titleRef}>
            <Text.H3>{t('viewAllProfiles.team')}</Text.H3>
          </PageLayoutStyles.PageTitleContainer>
        )}
        {sortedGroups.length === 0 ? (
          <Styled.EmptyState>
            <EmptyStateCard title={t('viewAllProfiles.noTeamProfiles')} />
          </Styled.EmptyState>
        ) : (
          sortedGroups.map((letter) => (
            <Styled.Group key={letter}>
              {!isDesktopView && <Text.P2Semibold>{letter}</Text.P2Semibold>}
              {groupedProfiles[letter].map((profile) => {
                const employmentNumber =
                  profile.employee_id || t('employeeProfile.notAvailable');

                return (
                  <Styled.ProfileItem
                    key={profile.id}
                    onClick={() => onProfileClick(profile.id)}
                    isActive={selectedProfileId === profile.id}
                    isSelected={false}
                  >
                    <Styled.ProfileAvatar>
                      <Text.P2Semibold>
                        {getInitials(profile.full_name)}
                      </Text.P2Semibold>
                    </Styled.ProfileAvatar>
                    <Styled.ProfileInfo>
                      <Text.P2Semibold>{profile.full_name}</Text.P2Semibold>
                      <Text.Label>{employmentNumber}</Text.Label>
                    </Styled.ProfileInfo>
                    {!isDesktopView && (
                      <Styled.ChevronIcon>
                        <Arrow.ChevronRight size={20} />
                      </Styled.ChevronIcon>
                    )}
                  </Styled.ProfileItem>
                );
              })}
            </Styled.Group>
          ))
        )}
      </Styled.Container>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        profiles={profiles}
        onProfileClick={onProfileClick}
      />
    </>
  );
}
